import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Notification, NotificationType } from './notification.model';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notifications = new BehaviorSubject<Notification[]>([]);
  private defaultDuration = 5000; // 5 seconds

  constructor() {}

  /**
   * Get active notifications
   */
  getNotifications(): Observable<Notification[]> {
    return this.notifications.asObservable();
  }

  /**
   * Show a success notification
   * @param message - Notification message
   * @param title - Notification title (optional)
   * @param duration - Duration in milliseconds (optional)
   */
  success(message: string, title: string = 'Success', duration?: number): string {
    return this.addNotification({
      type: 'success',
      message,
      title,
      duration: duration || this.defaultDuration
    });
  }

  /**
   * Show an error notification
   * @param message - Notification message
   * @param title - Notification title (optional)
   * @param duration - Duration in milliseconds (optional)
   */
  error(message: string, title: string = 'Error', duration?: number): string {
    return this.addNotification({
      type: 'error',
      message,
      title,
      duration: duration || this.defaultDuration
    });
  }

  /**
   * Show a warning notification
   * @param message - Notification message
   * @param title - Notification title (optional)
   * @param duration - Duration in milliseconds (optional)
   */
  warning(message: string, title: string = 'Warning', duration?: number): string {
    return this.addNotification({
      type: 'warning',
      message,
      title,
      duration: duration || this.defaultDuration
    });
  }

  /**
   * Show an info notification
   * @param message - Notification message
   * @param title - Notification title (optional)
   * @param duration - Duration in milliseconds (optional)
   */
  info(message: string, title: string = 'Info', duration?: number): string {
    return this.addNotification({
      type: 'info',
      message,
      title,
      duration: duration || this.defaultDuration
    });
  }

  /**
   * Close notification by id
   * @param id - Notification id
   */
  close(id: string): void {
    const currentNotifications = this.notifications.getValue();
    const updatedNotifications = currentNotifications.filter(
      notification => notification.id !== id
    );
    this.notifications.next(updatedNotifications);
  }

  /**
   * Clear all notifications
   */
  clearAll(): void {
    this.notifications.next([]);
  }

  /**
   * Add a new notification
   * @param notification - Notification data
   * @returns string - Notification id
   * @private
   */
  private addNotification(notification: Partial<Notification>): string {
    const id = this.generateId();
    const newNotification: Notification = {
      id,
      title: notification.title || '',
      message: notification.message || '',
      type: notification.type || 'info',
      duration: notification.duration,
      showProgress: notification.showProgress || true,
      createdAt: new Date()
    };

    const currentNotifications = this.notifications.getValue();

    this.notifications.next([...currentNotifications, newNotification]);

    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        this.close(id);
      }, newNotification.duration);
    }

    return id;
  }

  /**
   * Generate a unique ID for the notification
   * @private
   */
  private generateId(): string {
    return Math.random().toString(36).substring(2, 9);
  }
}
